import React, { useState, useContext, useEffect, useCallback } from 'react'
import { FormControlLabel, Checkbox, Typography, FormControl, TextField, MenuItem, FormGroup } from '@mui/material'
import CardHeader from './CardHeader'
import makeStyles from '@mui/styles/makeStyles';
import { DataRoomsContext, MessageContext } from './Contexts'
import { UpdateRoom } from 'modules/Rest'
import { setModes } from 'modules/Utils'
import { useSelectedDataRoom } from 'hooks/useSelectedDataRoom'

const useStyles = makeStyles(theme => ({
  FormGroup: {
    flex: 1,
    '&:first-child': {
      marginRight: theme.spacing(2),
    },
  },
  Select: {
    maxWidth: '50%'
  },
}))

function DataRoomSettingsCard() {
  const {  setRefreshNext, refreshNext, triggerRefresh } = useContext(DataRoomsContext)
  const { dataRoom } = useSelectedDataRoom()
  const { messageDispatch } = useContext(MessageContext)
  const [mode, setMode] = useState('')
  const classes = useStyles()
  const [searchable, setSearchable] = useState(dataRoom.searchable)
  const [searchable_content, setSearchableContent] = useState(dataRoom.searchable_content)
  const [redaction, setRedaction] = useState(dataRoom.redaction)
  const [two_factor_allowed, setTwoFactorAllowed] = useState(dataRoom.two_factor_allowed)
  const [qa, setQA] = useState(dataRoom.qa)
  const [digest_interval, setDigestInterval] = useState(dataRoom.digest_interval)
  const [max_update_hours, setMaxUpdateHours] = useState(dataRoom.max_update_hours)

  const setInitialState = useCallback(() => {
    setSearchable(dataRoom.searchable)
    setSearchableContent(dataRoom.searchable_content)
    setRedaction(dataRoom.redaction)
    setTwoFactorAllowed(dataRoom.two_factor_allowed)
    setQA(dataRoom.qa)
    setDigestInterval(dataRoom.digest_interval)
    setMaxUpdateHours(dataRoom.max_update_hours)
  }, [
    dataRoom.digest_interval,
    dataRoom.max_update_hours,
    dataRoom.qa,
    dataRoom.redaction,
    dataRoom.searchable,
    dataRoom.searchable_content,
    dataRoom.two_factor_allowed,
  ])

  useEffect(() => {
    setInitialState()
  }, [dataRoom.data_room, setInitialState])

  const submit = async (e, setMode) => {
    try {
      setMode('pending')
      const { data: { success, info } } = await UpdateRoom({
        data_room: dataRoom.data_room,
        searchable,
        searchable_content,
        redaction,
        two_factor_allowed,
        qa,
        digest_interval,
        max_update_hours,
      })
      if (success) {
        info && messageDispatch({ type: 'info', payload: info })
        setRefreshNext(true)
        setModes(setMode, ['completing', 1500, ''])
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      // Nothing was saved, so put the form back in sync with the data room
      revert(setMode)
      messageDispatch({ type: 'warning', payload: error.message })
    }
  }

  const revert = (setMode) => {
    if (refreshNext) {
      triggerRefresh()
    } else {
      setInitialState()
    }
    setMode('')
  }

  const cancel = (e, setMode) => revert(setMode)

  return (
    <>
      <CardHeader
        mode={mode}
        setMode={setMode}
        action={submit}
        cancel={cancel}
        title={dataRoom.light ? 'Notification timing' : 'Search'}
        headingElement='subtitle1'
        btnParams={{editTitle: 'Edit settings'}}
        noMargin={true}
      />
      <FormGroup className={classes.FormGroup}>
        {!dataRoom.light &&
         <>
           <FormControlLabel
             control={
               <Checkbox
                 checked={searchable}
                 onChange={event => setSearchable(event.target.checked)}
                 disabled={mode !== 'editing'}
               />
             }
             label='Enable file name search'
           />
           <FormControlLabel
             control={
               <Checkbox
                 checked={searchable_content}
                 onChange={event => setSearchableContent(event.target.checked)}
                 disabled={mode !== 'editing'}
               />
             }
             label='Enable PDF content search'
           />
           {dataRoom.allow_redaction &&
             <>
               <Typography variant='subtitle1'>
                 Redaction [BETA]
               </Typography>
               <FormControlLabel
                 control={
                   <Checkbox
                     checked={redaction}
                     onChange={event => setRedaction(event.target.checked)}
                     disabled={mode !== 'editing'}
                   />
                 }
                 label='Enable PDF redaction'
               />
             </>
           }
           <Typography variant='subtitle1'>
             Two-factor authentication
           </Typography>
           <FormControlLabel
             control={
               <Checkbox
                 checked={two_factor_allowed}
                 onChange={event => setTwoFactorAllowed(event.target.checked)}
                 disabled={mode !== 'editing'}
               />
             }
             label='Enable enforced two-factor authentication'
           />
           <Typography variant='subtitle1'>
             Q&A
           </Typography>
           <FormControlLabel
             control={<Checkbox checked={qa} onChange={event => setQA(event.target.checked)} disabled={mode !== 'editing'} />}
             label='Enable Q&A'
           />
         </>
        }

        {!dataRoom.light &&
          <Typography variant='subtitle1'>
            Notification timing
          </Typography>
        }
        <FormControl>
          <TextField
            select
            variant="standard"
            label="Hours"
            disabled={mode !== 'editing'}
            value={digest_interval}
            onChange={event => setDigestInterval(event.target.value)}
            className={classes.Select}
          >
            <MenuItem value={1}>1 hour</MenuItem>
            <MenuItem value={2}>2 hours</MenuItem>
            <MenuItem value={4}>4 hours</MenuItem>
            <MenuItem value={12}>12 hours</MenuItem>
            <MenuItem value={24}>24 hours</MenuItem>
          </TextField>
        </FormControl>
        <Typography variant='subtitle1'>
          Recently updated files
        </Typography>
        <FormControl>
          <TextField
            select
            variant="standard"
            disabled={mode !== 'editing'}
            value={max_update_hours}
            onChange={event => setMaxUpdateHours(event.target.value)}
            className={classes.Select}
            label="Only show updates made in the last"
          >
            <MenuItem value={-1}>No limit</MenuItem>
            <MenuItem value={12}>12 hours</MenuItem>
            <MenuItem value={24}>24 hours</MenuItem>
            <MenuItem value={48}>48 hours</MenuItem>
            <MenuItem value={72}>72 hours</MenuItem>
          </TextField>
        </FormControl>
      </FormGroup>
    </>
  )
}

export default DataRoomSettingsCard
